/**
 * useCategory Hook
 * 
 * Reusable hook for loading category details with presentation data.
 * Eliminates duplicated category loading logic across components.
 * 
 * @example
 * ```tsx
 * const { category, loading, error } = useCategory(expense.categoryId);
 * 
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage message={error} />;
 * if (category) return <CategoryChip category={category} />;
 * ```
 */

import { useState, useEffect } from 'react';
import type { CategoryViewModel } from '@/lib/domains';
import { CategoryService } from '@/lib/domains/category';
import { useExpenses } from '@/contexts/expense-context';
import { handleError } from '@/lib/utils/error-handler';
import { CATEGORY_CONFIG } from '@/lib/constants';

interface UseCategoryReturn {
    /** Category with presentation data (icon, color, display name) */
    category: CategoryViewModel | null;
    /** Loading state */
    loading: boolean;
    /** Error message if loading failed */
    error: string | null;
    /** Refetch category data */
    refetch: () => Promise<void>;
}

/**
 * Load a single category by ID with presentation data
 * 
 * Automatically handles:
 * - Loading state
 * - Error handling
 * - Theme detection (defaults to light)
 * - User authentication check
 * - Cleanup on unmount
 * 
 * @param categoryId - Optional category ID to load
 * @param theme - Theme for category colors/icons (defaults to 'light')
 * @returns Category data, loading state, error state, and refetch function
 */
export function useCategory(
    categoryId?: string,
    theme: 'light' | 'dark' = CATEGORY_CONFIG.DEFAULT_THEME
): UseCategoryReturn {
    const { currentUser } = useExpenses();
    const [category, setCategory] = useState<CategoryViewModel | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCategory = async () => {
        // Reset state
        setError(null);

        // Early return if no category ID or user
        if (!categoryId || !currentUser) {
            setCategory(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const cat = await CategoryService.getCategoryById(
                categoryId,
                currentUser.id,
                theme
            );

            setCategory(cat);
        } catch (err: unknown) {
            const errorMessage = handleError(err, 'Failed to load category');
            setError(errorMessage);
            setCategory(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategory();

        // Cleanup: reset state when component unmounts or categoryId changes
        return () => {
            setCategory(null);
            setError(null);
        };
    }, [categoryId, currentUser?.id, theme]);

    return {
        category,
        loading,
        error,
        refetch: fetchCategory,
    };
}

/**
 * Load multiple categories by IDs
 * 
 * Useful for loading categories for a list of expenses or recurring templates.
 * 
 * @param categoryIds - Array of category IDs to load
 * @param theme - Theme for category colors/icons
 * @returns Map of category ID to category data, loading state, error state
 */
export function useCategories(
    categoryIds: string[],
    theme: 'light' | 'dark' = CATEGORY_CONFIG.DEFAULT_THEME
): {
    categories: Map<string, CategoryViewModel>;
    loading: boolean;
    error: string | null;
} {
    const { currentUser } = useExpenses();
    const [categories, setCategories] = useState<Map<string, CategoryViewModel>>(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!currentUser || categoryIds.length === 0) {
            setCategories(new Map());
            setLoading(false);
            return;
        }

        const fetchCategories = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch all categories in parallel
                const promises = categoryIds.map(id =>
                    CategoryService.getCategoryById(id, currentUser.id, theme)
                        .then(cat => ({ id, cat }))
                        .catch(() => ({ id, cat: null })) // Don't fail on individual errors
                );

                const results = await Promise.all(promises);

                // Build map of successful results
                const categoryMap = new Map<string, CategoryViewModel>();
                results.forEach(({ id, cat }) => {
                    if (cat) {
                        categoryMap.set(id, cat);
                    }
                });

                setCategories(categoryMap);
            } catch (err: unknown) {
                setError(handleError(err, 'Failed to load categories'));
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [categoryIds.join(','), currentUser?.id, theme]);

    return { categories, loading, error };
}
